import { Box } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';

const MAX_FONT_SIZE = 16;
const MIN_FONT_SIZE = 12;
const MAX_LINES = 2;

interface LabelLayout {
    fontSize: number;
    lines: 1 | 2;
    isTruncated: boolean;
}

interface Props {
    text: string;
    onTruncatedChange?: (isTruncated: boolean) => void;
}

function getLineHeight(fontSize: number): number {
    return fontSize * 1.5;
}

function applySingleLineMeasureStyles(element: HTMLElement, fontSize: number, lineHeight: number) {
    element.style.fontSize = `${fontSize}px`;
    element.style.lineHeight = `${lineHeight}px`;
    element.style.maxHeight = `${lineHeight}px`;
    element.style.wordBreak = 'break-word';
    element.style.overflow = 'hidden';
    element.style.display = 'block';
    element.style.webkitLineClamp = 'unset';
    element.style.webkitBoxOrient = '';
    element.style.whiteSpace = 'nowrap';
    element.style.textOverflow = 'clip';
}

function clearMeasureStyles(element: HTMLElement) {
    element.style.fontSize = '';
    element.style.lineHeight = '';
    element.style.maxHeight = '';
    element.style.wordBreak = '';
    element.style.overflow = '';
    element.style.display = '';
    element.style.webkitLineClamp = '';
    element.style.webkitBoxOrient = '';
    element.style.whiteSpace = '';
    element.style.textOverflow = '';
}

function measureLabelLayout(element: HTMLElement): LabelLayout {
    for (let fontSize = MAX_FONT_SIZE; fontSize > MIN_FONT_SIZE; fontSize--) {
        const lineHeight = getLineHeight(fontSize);
        applySingleLineMeasureStyles(element, fontSize, lineHeight);

        if (element.scrollWidth <= element.clientWidth) {
            return { fontSize, lines: 1, isTruncated: false };
        }
    }

    const minLineHeight = getLineHeight(MIN_FONT_SIZE);
    applySingleLineMeasureStyles(element, MIN_FONT_SIZE, minLineHeight);

    if (element.scrollWidth <= element.clientWidth) {
        return { fontSize: MIN_FONT_SIZE, lines: 1, isTruncated: false };
    }

    // Measure full wrapped height without line-clamp so we can detect truncation.
    element.style.whiteSpace = 'normal';
    element.style.maxHeight = '';
    const isTruncated = element.scrollHeight > minLineHeight * MAX_LINES;

    return { fontSize: MIN_FONT_SIZE, lines: 2, isTruncated };
}

export default function SideNavWrappedLabel({ text, onTruncatedChange }: Props) {
    const labelRef = useRef<HTMLDivElement>(null);
    const [layout, setLayout] = useState<LabelLayout>({
        fontSize: MAX_FONT_SIZE,
        lines: 1,
        isTruncated: false,
    });

    useLayoutEffect(() => {
        const element = labelRef.current;
        if (!element) return;

        const nextLayout = measureLabelLayout(element);
        // Measurement writes inline styles that would override sx (including
        // text-overflow). Clear them so the rendered styles below take effect.
        clearMeasureStyles(element);
        setLayout(nextLayout);
        onTruncatedChange?.(nextLayout.isTruncated);
    }, [text, onTruncatedChange]);

    // Emotion can strip -webkit-box-orient from sx; set it imperatively so
    // multi-line ellipsis keeps working.
    useLayoutEffect(() => {
        const element = labelRef.current;
        if (!element) return;

        if (layout.lines === 2) {
            element.style.setProperty('display', '-webkit-box');
            element.style.setProperty('-webkit-box-orient', 'vertical');
            element.style.setProperty('-webkit-line-clamp', String(MAX_LINES));
        } else {
            element.style.removeProperty('display');
            element.style.removeProperty('-webkit-box-orient');
            element.style.removeProperty('-webkit-line-clamp');
        }
    }, [layout]);

    const lineHeight = getLineHeight(layout.fontSize);

    return (
        <Box
            ref={labelRef}
            sx={{
                minWidth: 0,
                fontSize: `${layout.fontSize}px`,
                lineHeight: `${lineHeight}px`,
                overflow: 'hidden',
                wordBreak: 'break-word',
                ...(layout.lines === 1
                    ? {
                          display: 'block',
                          maxHeight: `${lineHeight}px`,
                          whiteSpace: 'nowrap',
                      }
                    : {
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: MAX_LINES,
                          maxHeight: `${lineHeight * MAX_LINES}px`,
                          whiteSpace: 'normal',
                          textOverflow: 'ellipsis',
                      }),
            }}
        >
            {text}
        </Box>
    );
}
